import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from '../../context/HistoryContext';
import './HistoryDebugPanel.css';

const HistoryDebugPanel = ({ isVisible = false }) => {
  const { getHistoryInfo, flushPending, clearHistory, performCleanup } = useHistory();
  const [historyInfo, setHistoryInfo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  // Add confirmation dialog state
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  // Dragging state
  const [position, setPosition] = useState({ top: 10, left: window.innerWidth - 360 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      const updateInfo = () => {
        setHistoryInfo(getHistoryInfo());
      };
      
      updateInfo();
      const interval = setInterval(updateInfo, 1000); // Update every second
      
      return () => clearInterval(interval);
    }
  }, [isVisible, getHistoryInfo]);

  // Dragging functionality
  const handleMouseDown = (e) => {
    if (e.target.closest('.debug-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.left,
        y: e.clientY - position.top
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newLeft = e.clientX - dragStart.x;
      const newTop = e.clientY - dragStart.y;
      
      // Keep panel within viewport bounds
      const maxLeft = window.innerWidth - 250;
      const maxTop = window.innerHeight - 100;
      
      setPosition({
        left: Math.max(0, Math.min(newLeft, maxLeft)),
        top: Math.max(0, Math.min(newTop, maxTop))
      });
    }
  }, [isDragging, dragStart.x, dragStart.y]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleConfirmClearHistory = () => {
    clearHistory();
    setCleanupResult(null);
    setShowClearConfirm(false);
  };

  const handleCancelClearHistory = () => {
    setShowClearConfirm(false);
  };

  if (!isVisible || !historyInfo) return null;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div 
      ref={panelRef}
      className="history-debug-panel"
      style={{
        top: position.top,
        left: position.left,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
      onMouseDown={handleMouseDown}
    >
      <div 
        className="debug-header" 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <h4>History Debug Panel 📍</h4>
        <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
      </div>
      
      {isExpanded && (
        <div className="debug-content">
          <div className="debug-stats">
            <div className="stat-item">
              <label>Total Commands:</label>
              <span>{historyInfo.totalCommands}</span>
            </div>
            <div className="stat-item">
              <label>Pending Commands:</label>
              <span>{historyInfo.pendingCommands}</span>
            </div>
            <div className="stat-item">
              <label>Current Position:</label>
              <span>{historyInfo.currentPosition} / {historyInfo.totalCommands}</span>
            </div>
            <div className="stat-item">
              <label>Memory Usage:</label>
              <span>{formatBytes(historyInfo.memoryUsage)}</span>
            </div>
            <div className="stat-item">
              <label>Can Undo:</label>
              <span className={historyInfo.canUndo ? 'enabled' : 'disabled'}>
                {historyInfo.canUndo ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="stat-item">
              <label>Can Redo:</label>
              <span className={historyInfo.canRedo ? 'enabled' : 'disabled'}>
                {historyInfo.canRedo ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          
          <div className="debug-actions">
            <button onClick={flushPending} className="flush-btn">
              Flush Pending
            </button>
            <button 
              onClick={() => {
                const result = performCleanup();
                setCleanupResult(result);
                setTimeout(() => setCleanupResult(null), 5000); // Clear message after 5 seconds
              }} 
              className="cleanup-btn"
              title="Clean up memory and localStorage to improve performance"
            >
              🧹 Cleanup Memory
            </button>
            <button 
              onClick={() => setShowClearConfirm(true)}
              className="clear-btn"
            >
              Clear History
            </button>
          </div>
          
          {cleanupResult && (
            <div className="cleanup-result">
              <p>✅ Cleanup completed!</p>
              <p>Commands: {cleanupResult.before} → {cleanupResult.after}</p>
              <p>Memory freed: {formatBytes(cleanupResult.memoryFreed)}</p>
            </div>
          )}
          
          <div className="command-list">
            <h5>Recent Commands:</h5>
            <div className="commands">
              {historyInfo.commands.slice(-10).map((cmd, idx) => (
                <div 
                  key={cmd.id || idx} 
                  className={`command-item ${cmd.isCurrent ? 'current' : ''}`}
                >
                  <span className="command-type">{cmd.type}</span>
                  <span className="command-target">{cmd.targetId}</span>
                  <span className="command-time">
                    {new Date(cmd.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Clear History Confirmation Dialog */}
      {showClearConfirm && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Clear History</h3>
              <button 
                className="close-btn"
                onClick={handleCancelClearHistory}
              >
                ×
              </button>
            </div>
            <div className="confirmation-body">
              <p>Are you sure you want to clear all history?</p>
              <p className="warning-text">
                <i className="fas fa-exclamation-triangle"></i>
                This action cannot be undone.
              </p>
            </div>
            <div className="confirmation-actions">
              <button 
                className="confirm-btn cancel-btn"
                onClick={handleCancelClearHistory}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn delete-btn"
                onClick={handleConfirmClearHistory}
              >
                Clear History
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryDebugPanel; 